/**
 * The board profile, chained into one closed ring.
 *
 * The outline is stored as whatever line and arc segments happen to sit on the
 * pad master, in whatever order the file lists them and in either direction.
 * That is enough to draw, and not enough for anything that needs an *area*:
 * clipping a picture to it, extruding it into a slab, deciding where copper
 * may be poured. All of those need the segments joined end to end into a ring.
 *
 * This began life in the realistic views, which is where the need first showed
 * up. It belongs here because the flood pour needs the same answer — a pour
 * bounded by a rectangle spills off any board that is not one — and the two
 * must not disagree about where the board stops.
 */

import type { CadDocument } from '../model.ts';
import { outlineEntities } from './board.ts';

/** How close two ends must be to count as the same point. */
const JOIN = 0.005;

/** A degree step fine enough that an arc reads as a curve. */
const ARC_STEP = 6;

/**
 * The board profile as one closed ring, or null when it will not close.
 *
 * Returning null rather than guessing is deliberate: every caller has a
 * reasonable fallback in the board's bounding box, and a rectangle slightly
 * too generous is a far better failure than a ring that encloses the wrong
 * area. Chaining that *succeeds* on a self-crossing outline is the dangerous
 * case, so a ring enclosing very little of its own box is refused too.
 */
export function outlineRing(doc: CadDocument): { pts: [number, number][] } | null {
  const parts = outlineEntities(doc).filter((e) => e.kind === 'line' || e.kind === 'arc');
  if (parts.length === 0) return null;

  /** Each segment as a run of points, so an arc is walked rather than fitted. */
  const runs: [number, number][][] = [];
  for (const e of parts) {
    if (e.kind === 'line') {
      runs.push([[e.x1, e.y1], [e.x2, e.y2]]);
    } else if (e.kind === 'arc') {
      const sweep = e.sweep === 0 ? 360 : e.sweep;
      const steps = Math.max(2, Math.ceil(Math.abs(sweep) / ARC_STEP));
      const run: [number, number][] = [];
      for (let i = 0; i <= steps; i++) {
        const t = ((e.angle + (sweep * i) / steps) * Math.PI) / 180;
        run.push([e.cx + e.radius * Math.cos(t), e.cy + e.radius * Math.sin(t)]);
      }
      runs.push(run);
    }
  }

  const near = (a: [number, number], b: [number, number]): boolean =>
    Math.hypot(a[0] - b[0], a[1] - b[1]) < JOIN;

  const used = new Array(runs.length).fill(false);
  const ring: [number, number][] = [...runs[0]!];
  used[0] = true;
  for (let added = 1; added < runs.length; added++) {
    const tail = ring[ring.length - 1]!;
    let found = false;
    for (let i = 0; i < runs.length; i++) {
      if (used[i]) continue;
      const r = runs[i]!;
      if (near(tail, r[0]!)) { ring.push(...r.slice(1)); used[i] = true; found = true; break; }
      if (near(tail, r[r.length - 1]!)) {
        ring.push(...[...r].reverse().slice(1));
        used[i] = true; found = true; break;
      }
    }
    // A profile in more than one piece — a board with a cutout, or one whose
    // outline simply does not meet. Neither can be relied on.
    if (!found) return null;
  }
  // The chain arrives back where it started, so the last point repeats the
  // first. Closure is implied by this being a ring, and a repeated point is a
  // zero-length edge — which anything measuring edge directions has to special
  // case, or refuse. Drop it, and any other pair that landed on each other.
  // Not the joining tolerance: that is five thou, and a small arc flattened at
  // six degrees a step puts its points closer together than that. Using it
  // here ate the arcs and cost fifteen boards their profile. Only points
  // genuinely on top of each other go.
  const same = (a: [number, number], b: [number, number]): boolean =>
    Math.hypot(a[0] - b[0], a[1] - b[1]) < 1e-9;
  const clean: [number, number][] = [];
  for (const p of ring) {
    const last = clean[clean.length - 1];
    if (!last || !same(last, p)) clean.push(p);
  }
  while (clean.length > 1 && same(clean[0]!, clean[clean.length - 1]!)) clean.pop();
  ring.length = 0;
  ring.push(...clean);

  if (ring.length < 3) return null;

  // Chaining can succeed and still produce nonsense: segments that meet end to
  // end but double back leave a ring enclosing almost no area. A real profile
  // fills a good part of its own box, so one that does not is rejected.
  let cross = 0;
  let x1 = Infinity; let y1 = Infinity; let x2 = -Infinity; let y2 = -Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    cross += a[0] * b[1] - b[0] * a[1];
    x1 = Math.min(x1, a[0]); x2 = Math.max(x2, a[0]);
    y1 = Math.min(y1, a[1]); y2 = Math.max(y2, a[1]);
  }
  const box = (x2 - x1) * (y2 - y1);
  if (!(box > 0) || Math.abs(cross) / 2 < box * 0.2) return null;

  return { pts: ring };
}

/**
 * Shrink a closed ring inward by a fixed distance.
 *
 * Copper is not poured right up to the routed edge: the cutter would smear it
 * over the board's side, and a burr of copper on the edge of a board is both a
 * short waiting to happen and a reason for the fabricator to reject the job.
 * Ten to twenty thou of pull-back is the usual ask.
 *
 * Each edge is moved inward and the new corner is where the two moved edges
 * meet, which keeps the corners sharp — offsetting the points themselves
 * instead rounds every corner off by the inset and leaves the edges in the
 * wrong place. Returns null when the ring cannot survive the shrink: a big
 * inset on a thin board turns the polygon inside out, and no pour is a better
 * answer than one folded through itself.
 */
export function insetRing(
  pts: [number, number][], by: number,
): [number, number][] | null {
  if (!(by > 0)) return pts;
  if (pts.length < 3) return null;

  // A repeated point is a zero-length edge with no direction, and every corner
  // here is worked out from two edge directions. Rings arrive clean now, but a
  // caller handing one in from elsewhere should not be punished for it.
  const dedup: [number, number][] = [];
  for (const p of pts) {
    const last = dedup[dedup.length - 1];
    if (!last || Math.hypot(last[0] - p[0], last[1] - p[1]) > 1e-9) dedup.push(p);
  }
  while (dedup.length > 1
    && Math.hypot(dedup[0]![0] - dedup[dedup.length - 1]![0],
      dedup[0]![1] - dedup[dedup.length - 1]![1]) < 1e-9) dedup.pop();
  if (dedup.length < 3) return null;
  pts = dedup;

  const area = (p: [number, number][]): number => {
    let s = 0;
    for (let i = 0; i < p.length; i++) {
      const a = p[i]!;
      const b = p[(i + 1) % p.length]!;
      s += a[0] * b[1] - b[0] * a[1];
    }
    return s / 2;
  };

  // Anticlockwise, so the inside is always to the left of a directed edge.
  const flipped = area(pts) < 0;
  const ring = flipped ? [...pts].reverse() : pts;

  const n = ring.length;
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const prev = ring[(i + n - 1) % n]!;
    const here = ring[i]!;
    const next = ring[(i + 1) % n]!;

    // The two edges meeting at this corner, each moved inward by `by`.
    const line = (a: [number, number], b: [number, number]) => {
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const len = Math.hypot(dx, dy);
      if (len < 1e-12) return null;
      const nx = -dy / len;          // left of the direction of travel
      const ny = dx / len;
      return { px: a[0] + nx * by, py: a[1] + ny * by, dx: dx / len, dy: dy / len };
    };
    const a = line(prev, here);
    const b = line(here, next);
    if (!a || !b) return null;

    // Where the two moved edges cross.
    const det = a.dx * b.dy - a.dy * b.dx;
    if (Math.abs(det) < 1e-9) {
      // Straight through, or as near as makes no difference: no corner to
      // find, so the edge's own offset is the answer.
      out.push([b.px, b.py]);
      continue;
    }
    const t = ((b.px - a.px) * b.dy - (b.py - a.py) * b.dx) / det;
    out.push([a.px + a.dx * t, a.py + a.dy * t]);
  }

  // The shrink must have shrunk it, and must not have turned it inside out.
  const was = Math.abs(area(ring));
  const now = area(out);
  if (!(now > 0) || now >= was || now < was * 0.02) return null;

  return flipped ? out.reverse() : out;
}
